import type { ReceivingEnvironment } from 'api/management/receivingSetup';

export const ATTEMPT_KEY = 'zespy.receiving.browserAttempt.v1';
export const RETURN_PATH = '/management/receiving-payments/stripe-return';
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export interface AttemptIdentity { issuer: string; subject: string; organization: string; tenantId: string }
export interface BrowserAttempt extends AttemptIdentity {
  operationId: string; browserVerifier: string; environment: ReceivingEnvironment;
  expiresUtc: string; revision?: number;
}
export const clearBrowserAttempt = () => { try { sessionStorage.removeItem(ATTEMPT_KEY); } catch { /* No alternate custody. */ } };
export const sameIdentity = (a: AttemptIdentity, b: AttemptIdentity) => a.issuer === b.issuer
  && a.subject === b.subject && a.organization === b.organization && a.tenantId === b.tenantId;
export const validIdentity = (a: AttemptIdentity) => /^https:\/\/[^\s]{1,256}\/$/.test(a.issuer)
  && typeof a.subject === 'string' && a.subject.length > 0 && a.subject.length <= 256 && !/\s/.test(a.subject)
  && /^org_[A-Za-z0-9]{1,128}$/.test(a.organization) && uuid.test(a.tenantId);
export const readBrowserAttempt = (): BrowserAttempt | null => {
  try {
    const raw = sessionStorage.getItem(ATTEMPT_KEY);
    if (!raw || raw.length > 2048) return null;
    const a = JSON.parse(raw) as BrowserAttempt;
    if (!a || !validIdentity(a) || !uuid.test(a.operationId) || !/^[A-Za-z0-9_-]{43}$/.test(a.browserVerifier)
      || !['Test', 'Live'].includes(a.environment) || !Number.isFinite(Date.parse(a.expiresUtc))
      || Date.parse(a.expiresUtc) <= Date.now() || Date.parse(a.expiresUtc) > Date.now() + 15 * 60_000
      || (a.revision !== undefined && (!Number.isSafeInteger(a.revision) || a.revision < 1))) {
      clearBrowserAttempt(); return null;
    }
    // Projection drops incidental fields; no callback material is read from storage.
    return { issuer: a.issuer, subject: a.subject, organization: a.organization, tenantId: a.tenantId,
      operationId: a.operationId, browserVerifier: a.browserVerifier, environment: a.environment,
      expiresUtc: a.expiresUtc, ...(a.revision === undefined ? {} : { revision: a.revision }) };
  } catch { clearBrowserAttempt(); return null; }
};
export const storeBrowserAttempt = (a: BrowserAttempt) => {
  if (!validIdentity(a)) throw new Error('Approval attempt cannot be saved.');
  sessionStorage.setItem(ATTEMPT_KEY, JSON.stringify({ issuer: a.issuer, subject: a.subject,
    organization: a.organization, tenantId: a.tenantId, operationId: a.operationId,
    browserVerifier: a.browserVerifier, environment: a.environment, expiresUtc: a.expiresUtc, revision: a.revision }));
};
export const createBrowserAttempt = (identity: AttemptIdentity, environment: ReceivingEnvironment): BrowserAttempt => {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  const browserVerifier = btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  return { ...identity, environment, operationId: crypto.randomUUID(), browserVerifier,
    expiresUtc: new Date(Date.now() + 5 * 60_000).toISOString() };
};
